// Versioned changes to the referential: propose (reviewer or above), approve or
// reject (administrator), history. The value in force is the latest approved one.
import { useState } from "react";
import { Check, History, Pencil, X } from "lucide-react";
import { api } from "../api/client";
import { invalidate, useMutation, useResource } from "../api/resource";
import type { ReferentialVersionItem } from "../api/types";
import { useCurrentUser } from "../auth";
import { formatDateTime, w } from "../i18n";
import { Dialog, SidePanel } from "./overlay";
import { ErrorState, SkeletonBlock, StatusBadge } from "./ui";

type Kind = "article" | "section";

function ProposeDialog({ kind, itemKey, label, current, onClose }: {
  kind: Kind; itemKey: string; label: string; current: string; onClose: () => void;
}) {
  const [value, setValue] = useState(current);
  const [reason, setReason] = useState("");
  const propose = useMutation(api.proposeReferential);
  const send = async () => {
    if (await propose.run({ kind, key: itemKey, value: value.trim(), reason: reason.trim() })) { invalidate("referential"); onClose(); }
  };
  return <Dialog title={w("refv.proposeTitle", { label })} onClose={onClose} size="md"
    footer={<><button className="ws-btn" onClick={onClose}>{w("common.cancel")}</button>
      <button className="ws-btn primary" disabled={propose.pending || !reason.trim() || !value.trim() || value.trim() === current.trim()}
        onClick={send}>{w("refv.submit")}</button></>}>
    <div className="ws-field"><label htmlFor="refv-value">{w(kind === "article" ? "refv.newCriteria" : "refv.newInstructions")}</label>
      <textarea id="refv-value" className="ws-textarea" rows={6} value={value} onChange={e => setValue(e.target.value)} data-autofocus /></div>
    <div className="ws-field"><label htmlFor="refv-reason">{w("refv.reason")}</label>
      <textarea id="refv-reason" className="ws-textarea" rows={3} value={reason} onChange={e => setReason(e.target.value)} /></div>
    <p className="ws-small ws-muted" style={{ margin: 0 }}>{w("refv.proposeNote")}</p>
    {propose.error && <ErrorState title={w("refv.error")} error={propose.error} />}
  </Dialog>;
}

function HistoryPanel({ kind, itemKey, label, onClose }: { kind: Kind; itemKey: string; label: string; onClose: () => void }) {
  const history = useResource(`referential-history:${kind}:${itemKey}`, () => api.referentialHistory(kind, itemKey));
  return <SidePanel title={w("refv.history")} subtitle={label} onClose={onClose}>
    {history.loading && <SkeletonBlock lines={4} />}
    {history.error && <ErrorState title={w("common.genericError")} error={history.error} onRetry={history.reload} />}
    {history.data && !history.data.items.length && <p className="ws-muted">{w("refv.noHistory")}</p>}
    <ol className="ws-plan-items">{(history.data?.items || []).map(v => <li key={v.id} className="ws-plan-item">
      <div className="ws-row" style={{ justifyContent: "space-between" }}><strong>{w("refv.version", { n: v.version_number })}</strong>
        <StatusBadge status={v.status === "approved" ? "approved" : v.status === "rejected" ? "failed" : "needs_review"}
          label={w(`refv.status.${v.status}`)} /></div>
      <div className="ws-small ws-muted">{w("refv.proposedBy", { actor: v.proposed_by, date: formatDateTime(v.proposed_at) })}
        {v.decided_by && ` · ${w("refv.decidedBy", { actor: v.decided_by, date: formatDateTime(v.decided_at) })}`}</div>
      <div className="ws-small" style={{ marginTop: 4 }}><em>{v.reason}</em></div>
      <div className="ws-small" style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{v.value}</div>
      {v.decision_comment && <div className="ws-small ws-muted" style={{ marginTop: 4 }}>{v.decision_comment}</div>}
    </li>)}</ol>
  </SidePanel>;
}

/** Version badge, pending proposal with its diff and decision, and the actions of one referential item. */
export function VersionedCell({ kind, itemKey, label, current, version, pending }: {
  kind: Kind; itemKey: string; label: string; current: string; version: number; pending: ReferentialVersionItem | null;
}) {
  const { can } = useCurrentUser();
  const [proposing, setProposing] = useState(false);
  const [showing, setShowing] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState("");
  const decide = useMutation(api.decideReferential);
  const close = async (decision: "approved" | "rejected") => {
    if (await decide.run(pending!.id, decision, comment.trim())) { setRejecting(false); setComment(""); invalidate("referential", "referential-history:"); }
  };
  return <>
    <div className="ws-small" style={{ whiteSpace: "pre-wrap" }}>{current}</div>
    <div className="ws-row" style={{ marginTop: 6, gap: 6, flexWrap: "wrap" }}>
      <span className="ws-tag">{version ? w("refv.version", { n: version }) : w("refv.default")}</span>
      {!pending && <button className="ws-btn sm ghost" disabled={!can("reviewer")}
        title={can("reviewer") ? undefined : w("auth.needs", { role: w("role.user.reviewer") })}
        onClick={() => setProposing(true)}><Pencil size={13} />{w("refv.propose")}</button>}
      <button className="ws-btn sm ghost" onClick={() => setShowing(true)}><History size={13} />{w("refv.history")}</button>
    </div>
    {pending && <div className="ws-refv-pending">
      <div className="ws-small"><strong>{w("refv.pendingTitle", { n: pending.version_number, actor: pending.proposed_by })}</strong></div>
      <div className="ws-small ws-muted"><em>{pending.reason}</em></div>
      <div className="ws-diff"><del>{pending.previous_value}</del><ins>{pending.value}</ins></div>
      {rejecting && <textarea className="ws-textarea" rows={2} placeholder={w("refv.rejectReason")} aria-label={w("refv.rejectReason")}
        value={comment} onChange={e => setComment(e.target.value)} />}
      {can("admin") && <div className="ws-row" style={{ gap: 6, marginTop: 6 }}>
        <button className="ws-btn sm success" disabled={decide.pending} onClick={() => close("approved")}><Check size={13} />{w("refv.approve")}</button>
        {!rejecting
          ? <button className="ws-btn sm" disabled={decide.pending} onClick={() => setRejecting(true)}><X size={13} />{w("refv.reject")}</button>
          : <button className="ws-btn sm danger" disabled={decide.pending || !comment.trim()} onClick={() => close("rejected")}>{w("refv.confirmReject")}</button>}
      </div>}
      {!can("admin") && <div className="ws-small ws-faint">{w("refv.waitingAdmin")}</div>}
      {decide.error && <ErrorState title={w("refv.error")} error={decide.error} />}
    </div>}
    {proposing && <ProposeDialog kind={kind} itemKey={itemKey} label={label} current={current} onClose={() => setProposing(false)} />}
    {showing && <HistoryPanel kind={kind} itemKey={itemKey} label={label} onClose={() => setShowing(false)} />}
  </>;
}
